'use client';

import { useState, useEffect, useRef } from 'react';
import { useStore } from '@/lib/storeContext';
import { useAuth } from '@/lib/authContext';
import { getApiClient } from 'saletrax-shared/clients/apiClient';

// Simple SVG icons
const XIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

interface TourStep {
  id: string;
  title: string;
  content: string;
  target?: string; // CSS selector for element to highlight
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to the Marketplace!',
    content: 'We\'re excited to have you here. Let\'s take a quick tour to help you get started with shopping for fresh produce.',
    position: 'center'
  },
  {
    id: 'catalog',
    title: 'Browse Our Catalog',
    content: 'Explore our wide selection of fresh fruits, vegetables, and more. Click on any item to see details and add it to your cart.',
    target: 'a[href="/catalog"], a[href*="catalog"]',
    position: 'bottom'
  },
  {
    id: 'products',
    title: 'Product Details',
    content: 'When you find something you like, click on it to see full details, pricing, and add it to your cart.',
    position: 'bottom'
  },
  {
    id: 'cart',
    title: 'Your Shopping Cart',
    content: 'All your selected items will appear in your cart. Review your order before checkout.',
    target: 'a[href="/cart"], a[href*="cart"]',
    position: 'bottom'
  },
  {
    id: 'checkout',
    title: 'Easy Checkout',
    content: 'When you\'re ready, proceed to checkout. We\'ll handle the rest and get your order to you quickly.',
    position: 'bottom'
  },
  {
    id: 'account',
    title: 'Your Account',
    content: 'Manage your profile, view order history, and update your preferences from your account page.',
    position: 'bottom'
  }
];

interface OnboardingTourProps {
  onComplete: () => void;
}

export default function OnboardingTour({ onComplete }: OnboardingTourProps) {
  const store = useStore();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentStep < tourSteps.length) {
      const step = tourSteps[currentStep];
      if (step.target) {
        // Scroll to target element
        const targetElement = document.querySelector(step.target);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = async () => {
    setIsVisible(false);
    
    // Mark customer as signed up via backend
    if (user) {
      try {
        const api = getApiClient();
        // The profile endpoint updates VendorCustomer — signedUp is already set during invite acceptance
        // but we ensure it's set here too in case the tour was shown for another reason
        await api.get('/auth/marketplace/profile');
      } catch {
        // Error updating customer marketplace status
      }
    }
    
    onComplete();
  };

  if (!isVisible) return null;

  const step = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  // Calculate tooltip position
  let tooltipStyle: React.CSSProperties = {};
  if (step.target) {
    const targetElement = document.querySelector(step.target);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      
      switch (step.position) {
        case 'top':
          tooltipStyle = {
            position: 'absolute',
            top: rect.top + scrollY - 20,
            left: rect.left + scrollX + rect.width / 2,
            transform: 'translate(-50%, -100%)'
          };
          break;
        case 'bottom':
          tooltipStyle = {
            position: 'absolute',
            top: rect.bottom + scrollY + 20,
            left: rect.left + scrollX + rect.width / 2,
            transform: 'translateX(-50%)'
          };
          break;
        case 'left':
          tooltipStyle = {
            position: 'absolute',
            top: rect.top + scrollY + rect.height / 2,
            left: rect.left + scrollX - 20,
            transform: 'translate(-100%, -50%)'
          };
          break;
        case 'right':
          tooltipStyle = {
            position: 'absolute',
            top: rect.top + scrollY + rect.height / 2,
            left: rect.right + scrollX + 20,
            transform: 'translateY(-50%)'
          };
          break;
      }
    }
  } else {
    // Center position
    tooltipStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 10000
    };
  }

  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black/50 z-[9998]"
        onClick={step.position === 'center' ? undefined : handleNext}
      />
      
      {/* Highlight overlay for target element */}
      {step.target && (() => {
        const targetElement = document.querySelector(step.target!);
        if (targetElement) {
          const rect = targetElement.getBoundingClientRect();
          return (
            <div
              className="fixed border-2 border-black rounded-lg pointer-events-none z-[9999]"
              style={{
                top: rect.top + window.scrollY - 8,
                left: rect.left + window.scrollX - 8,
                width: rect.width + 16,
                height: rect.height + 16,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
              }}
            />
          );
        }
        return null;
      })()}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed bg-white rounded-lg shadow-xl p-6 max-w-sm z-[10000]"
        style={tooltipStyle}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
            <p className="text-gray-600 text-sm">{step.content}</p>
          </div>
          <button
            onClick={handleSkip}
            className="ml-4 text-gray-400 hover:text-gray-600"
            aria-label="Skip tour"
          >
            <XIcon />
          </button>
        </div>
        
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-500">
            Step {currentStep + 1} of {tourSteps.length}
          </div>
          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <button
                onClick={handlePrevious}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-1"
              >
                <ArrowLeftIcon />
                Previous
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-1"
              style={{ backgroundColor: store.themeColor }}
            >
              {isLastStep ? (
                <>
                  <CheckCircleIcon />
                  Get Started
                </>
              ) : (
                <>
                  Next
                  <ArrowRightIcon />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

